import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useInstallments } from "@/lib/useInstallmentsCache";
import { useCommitments } from "@/lib/useCommitmentsCache";
import { RecurringDetails } from "@/components/dashboard/RecurringDetails";

// Commitment Health Gauge — 3D neumorphic ring, positioned opposite to text side.
export function NeuBudgetDial({ summary }) {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const inst = useInstallments();
  const cmt = useCommitments();
  const [showDetails, setShowDetails] = useState(false);
  const commitments = (inst.total_monthly || 0) + (cmt.total_monthly || 0);
  const income = summary?.this_month?.income || 0;
  const isAr = lang === "ar";

  const ratio = income > 0 ? commitments / income : 0;
  const pct = Math.min(1, ratio);
  const color = income <= 0 ? "#64748b" : ratio < 0.3 ? "#16a34a" : ratio < 0.5 ? "#b45309" : "#991b1b";
  const R = 82, C = 2 * Math.PI * R, offset = C * (1 - pct), stroke = 26;
  const gid = color.replace('#', '');

  const healthKey = income <= 0 ? "noIncomeYet" : ratio < 0.3 ? "healthGood" : ratio < 0.5 ? "healthWarn" : "healthRisk";
  const hint = isAr ? "قسط نسبة إلى الدخل" : t("ofIncome");
  const cur = user?.default_currency || "AED";
  const fmt = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(n));

  return (
    <div data-testid="commitment-health-dial" className="neu p-4 sm:p-5 h-full">
      <div className="flex items-center gap-4">
        <div className="flex-1 min-w-0 text-start space-y-1">
          <p className="font-display text-lg font-bold neu-accent-blue leading-tight">{t("commitmentHealth")}</p>
          <p className="font-display font-bold text-base sm:text-lg leading-tight" style={{ color }}>{t(healthKey)}</p>
          <p className="text-xs sm:text-sm text-neu-muted leading-snug">{hint}</p>
          {income > 0 && (
            <p data-testid="dial-breakdown" className="text-xs sm:text-sm font-semibold tabular-nums text-neu-ink/80 leading-snug">
              {fmt(commitments)} <span className="opacity-60">/</span> {fmt(income)} {cur}
            </p>
          )}
        </div>
        <div className="relative w-[120px] aspect-square shrink-0 rounded-full flex items-center justify-center cursor-pointer active:scale-95 transition-transform"
             onClick={() => setShowDetails(true)} role="button" data-testid="budget-dial-ring"
             style={{ background: "#eceef1", boxShadow: "inset 6px 6px 12px #c8cad0, inset -6px -6px 12px #ffffff" }}>
          <svg viewBox="0 0 220 220" className="w-[92%] h-[92%] -rotate-90">
            <defs>
              <linearGradient id={`track-${gid}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#b8babf" />
                <stop offset="55%" stopColor="#d8dade" />
                <stop offset="100%" stopColor="#ffffff" />
              </linearGradient>
              <linearGradient id={`prog-${gid}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={color} stopOpacity="1" />
                <stop offset="100%" stopColor={color} stopOpacity="0.6" />
              </linearGradient>
              <filter id={`ringShadow-${gid}`} x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="2" dy="3" stdDeviation="2.5" floodColor="#000000" floodOpacity="0.22" />
                <feDropShadow dx="-1" dy="-1" stdDeviation="1.5" floodColor="#ffffff" floodOpacity="0.9" />
              </filter>
            </defs>
            <circle cx="110" cy="110" r={R} fill="none" stroke={`url(#track-${gid})`} strokeWidth={stroke} />
            <circle cx="110" cy="110" r={R} fill="none" stroke={`url(#prog-${gid})`} strokeWidth={stroke}
              strokeLinecap="round" strokeDasharray={C} strokeDashoffset={offset}
              filter={`url(#ringShadow-${gid})`}
              style={{ transition: "stroke-dashoffset 700ms ease, stroke 400ms ease" }} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="font-display font-extrabold tabular-nums leading-none"
               style={{ color, fontSize: "1.5rem", textShadow: "1px 1px 2px rgba(255,255,255,0.95), -1px -1px 2px rgba(0,0,0,0.1)" }}>
              {income > 0 ? `${Math.round(ratio * 100)}%` : "—"}
            </p>
          </div>
        </div>
      </div>
      <RecurringDetails open={showDetails} onClose={() => setShowDetails(false)} ccy={cur} />
    </div>
  );
}
